/**
 * SmartQueueService.ts
 *
 * API client for the Smart Priority Queue system.
 * Handles fetching queue items, executing actions, and real-time updates.
 *
 * Created: 2026-01-05
 */

import { http } from './api';
import { API, DEFAULTS } from '@/config/constants';
import type {
  QueueItem,
  QueueStats,
  ActionResult,
  QueueFilters,
  ExecuteActionInput,
  DismissActionInput,
  SnoozeActionInput,
} from '@/types/queue';

class SmartQueueService {
  /**
   * Get prioritized queue items
   */
  async getQueue(filters?: QueueFilters): Promise<QueueItem[]> {
    const query: Record<string, string | number | undefined> = {};

    if (filters?.action_types && filters.action_types.length > 0) {
      query.action_types = filters.action_types.join(',');
    }
    if (filters?.min_priority !== undefined) {
      query.min_priority = filters.min_priority;
    }
    query.limit = filters?.limit ?? DEFAULTS.QUEUE_LIMIT;

    return http<QueueItem[]>(API.QUEUE, { method: 'GET', query });
  }

  /**
   * Get queue statistics
   */
  async getStats(): Promise<QueueStats> {
    return http<QueueStats>(`${API.QUEUE}/stats`, { method: 'GET' });
  }

  /**
   * Get a specific queue item by ID
   */
  async getItem(itemId: string): Promise<QueueItem> {
    return http<QueueItem>(`${API.QUEUE}/${itemId}`, { method: 'GET' });
  }

  /**
   * Execute the primary action for a queue item (swipe right)
   */
  async executeAction(
    itemId: string,
    input?: ExecuteActionInput
  ): Promise<ActionResult> {
    return http<ActionResult>(`${API.QUEUE}/${itemId}/execute`, {
      method: 'POST',
      body: JSON.stringify(input || {}),
    });
  }

  /**
   * Dismiss a queue item (swipe left)
   */
  async dismissAction(
    itemId: string,
    input?: DismissActionInput
  ): Promise<ActionResult> {
    return http<ActionResult>(`${API.QUEUE}/${itemId}/dismiss`, {
      method: 'POST',
      body: JSON.stringify(input || {}),
    });
  }

  /**
   * Snooze a queue item for later
   */
  async snoozeAction(
    itemId: string,
    input?: SnoozeActionInput
  ): Promise<ActionResult> {
    return http<ActionResult>(`${API.QUEUE}/${itemId}/snooze`, {
      method: 'POST',
      body: JSON.stringify({
        hours: input?.hours || DEFAULTS.SNOOZE_DEFAULT_HOURS,
      }),
    });
  }
}

export default new SmartQueueService();
